import { useEffect, useMemo, useRef, useState } from "react";

import { Dialog } from "../Dialog.jsx";
import { Icon } from "../Icon.jsx";
import { DoItemQuantityEditor } from "./DoItemQuantityEditor.jsx";

const STATUS_LABELS = {
  READY_TO_CREATE: "Siap direview",
  ALREADY_CREATED: "DO sudah dibuat",
  BLOCKED_STOCK: "Stok tidak mencukupi",
  BLOCKED_SOURCE_CHANGED: "Dokumen berubah",
  BLOCKED_WAREHOUSE: "Menunggu gudang",
  BLOCKED_ASSIGNMENT: "Menunggu data armada",
  BLOCKED_CONTRACT: "Data belum lengkap",
};

const GLOBAL_BLOCKER_CODES = new Set([
  "DO_DATE_REQUIRED",
  "DRIVER_ASSIGNMENT_REQUIRED",
  "VEHICLE_REQUIRED",
  "WAREHOUSE_MAPPING_REQUIRED",
]);

function formatQuantity(value) {
  if (value === null || value === undefined || value === "") return "—";
  return new Intl.NumberFormat("id-ID", { maximumFractionDigits: 4 }).format(
    Number(value) || 0,
  );
}

function formatDisplayDate(value) {
  if (!value) return "Belum tersedia";

  const normalized = String(value).trim();
  const slashMatch = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  const parsed = slashMatch
    ? new Date(`${slashMatch[3]}-${slashMatch[2]}-${slashMatch[1]}T00:00:00`)
    : new Date(normalized.length === 10 ? `${normalized}T00:00:00` : normalized);

  if (Number.isNaN(parsed.getTime())) return normalized;

  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

function statusClass(status) {
  return String(status || "unknown")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-");
}

function isLegacyLog(value) {
  return String(value || "").trim().toUpperCase() === "LOG";
}

function displayOperationalValue(value, fallback) {
  const normalized = String(value || "").trim();
  return normalized && !isLegacyLog(normalized) ? normalized : fallback;
}

function displayCoDriverValue(value, blockers = []) {
  const hasInvalidAssignment = (blockers || []).some(
    (blocker) => blocker?.code === "CO_DRIVER_ASSIGNMENT_REQUIRED",
  );
  if (hasInvalidAssignment) return "Placeholder LOG — perlu diperbaiki";
  return displayOperationalValue(value, "Tanpa Co-driver");
}

function getProfilePresentation(profileStatus, profileBusy, profileError) {
  if (profileBusy) {
    return {
      label: "Memeriksa…",
      tone: "checking",
      detail: "Memastikan koneksi Accurate tersedia.",
    };
  }

  if (profileError) {
    return {
      label: "Koneksi terganggu",
      tone: "attention",
      detail: "Coba periksa kembali. Hubungi administrator bila masalah berulang.",
    };
  }

  if (!profileStatus) {
    return {
      label: "Belum diperiksa",
      tone: "neutral",
      detail: "Status koneksi belum tersedia.",
    };
  }

  if (profileStatus.ready) {
    return {
      label: "Terhubung",
      tone: "ready",
      detail: "Pemeriksaan Delivery Order tersedia.",
    };
  }

  if (profileStatus.status === "NOT_CONFIGURED" || !profileStatus.configured) {
    return {
      label: "Belum tersedia",
      tone: "attention",
      detail: "Hubungi administrator untuk mengaktifkan koneksi Accurate.",
    };
  }

  if (profileStatus.status === "MISSING_SCOPES") {
    return {
      label: "Izin belum lengkap",
      tone: "attention",
      detail: "Hubungi administrator untuk melengkapi izin Delivery Order.",
    };
  }

  return {
    label: "Perlu diperiksa",
    tone: "attention",
    detail: "Koneksi Accurate belum dapat diverifikasi.",
  };
}

function getGlobalBlockerCodes(sources) {
  const unfinished = sources.filter(
    (source) => source.readinessStatus !== "ALREADY_CREATED",
  );

  if (!unfinished.length) return new Set();

  const commonCodes = new Set(
    (unfinished[0]?.blockers || []).map((blocker) => blocker.code),
  );

  unfinished.slice(1).forEach((source) => {
    const sourceCodes = new Set((source.blockers || []).map((blocker) => blocker.code));
    [...commonCodes].forEach((code) => {
      if (!sourceCodes.has(code)) commonCodes.delete(code);
    });
  });

  return new Set([...commonCodes].filter((code) => GLOBAL_BLOCKER_CODES.has(code)));
}

function getAssignmentBlockers(globalCodes) {
  return [
    globalCodes.has("DRIVER_ASSIGNMENT_REQUIRED")
      ? {
          title: "Driver belum ditentukan",
          detail: "Pilih driver pada Fleet Order sebelum membuat Delivery Order.",
        }
      : null,
    globalCodes.has("VEHICLE_REQUIRED")
      ? {
          title: "Kendaraan belum ditentukan",
          detail: "Pilih kendaraan dan nomor polisi pada Fleet Order.",
        }
      : null,
    globalCodes.has("DO_DATE_REQUIRED")
      ? {
          title: "Tanggal berangkat belum tersedia",
          detail: "Lengkapi tanggal berangkat Fleet Order untuk menentukan tanggal DO.",
        }
      : null,
  ].filter(Boolean);
}

function canShowTechnicalDetails() {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("debug") === "1";
}

const WRITE_STATUS_PRESENTATIONS = {
  VALIDATING: {
    tone: "processing",
    icon: "sync",
    title: "Menyiapkan pembuatan Delivery Order",
    detail: "Sistem sedang memeriksa ulang dokumen sebelum dikirim ke Accurate.",
  },
  SENDING: {
    tone: "processing",
    icon: "sync",
    title: "Sedang membuat Delivery Order",
    detail: "Permintaan sedang diproses. Jangan menutup halaman atau mencoba kembali.",
  },
  RECONCILING: {
    tone: "processing",
    icon: "sync",
    title: "Sedang memeriksa hasil di Accurate",
    detail: "Sistem memastikan apakah Delivery Order sudah terbentuk.",
  },
  RECONCILIATION_REQUIRED: {
    tone: "attention",
    icon: "warning",
    title: "Hasil pembuatan perlu diperiksa",
    detail:
      "Permintaan sudah dikirim, tetapi hasilnya belum dapat dipastikan. Jangan membuat ulang Delivery Order.",
  },
  FAILED: {
    tone: "error",
    icon: "warning",
    title: "Delivery Order gagal dibuat",
    detail:
      "Periksa penyebab kegagalan. Setelah data diperbaiki, proses dapat dicoba kembali.",
  },
  CREATED: {
    tone: "success",
    icon: "check",
    title: "Delivery Order berhasil dibuat",
    detail: "Hasil transaksi sudah diverifikasi di Accurate.",
  },
  DUPLICATE_LINKED: {
    tone: "success",
    icon: "check",
    title: "Delivery Order ditemukan di Accurate",
    detail: "Transaksi yang sudah terbentuk berhasil dihubungkan ke Fleet Order.",
  },
};

const WRITE_EVENT_LABELS = {
  REQUEST_CREATED: "Persiapan pembuatan dimulai",
  WRITE_STARTED: "Permintaan dikirim ke Accurate",
  WRITE_VERIFIED: "Delivery Order berhasil diverifikasi",
  WRITE_FAILED: "Pembuatan Delivery Order gagal",
  RETRY_REQUESTED: "Pembuatan Delivery Order dicoba kembali",
  WRITE_OUTCOME_UNCERTAIN: "Hasil transaksi perlu diperiksa",
  RECONCILIATION_STARTED: "Pemeriksaan hasil dimulai",
  RECONCILIATION_MATCHED: "Delivery Order ditemukan dan dihubungkan",
  RECONCILIATION_NOT_FOUND: "Delivery Order belum ditemukan",
  RECONCILIATION_AMBIGUOUS: "Ditemukan lebih dari satu transaksi yang cocok",
  RECONCILIATION_FAILED: "Pemeriksaan hasil gagal",
};

function formatWriteTimestamp(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function getWritePresentation(request) {
  if (!request?.status) return null;
  return WRITE_STATUS_PRESENTATIONS[String(request.status).toUpperCase()] || null;
}

export function DoWriteSourcePreviewDialog({
  fleetOrderWarehouse = null,
  onClose,
  onCreate,
  onItemWarehouseChange,
  onReconcile,
  onSourceWarehouseChange,
  source,
  warehouses = [],
  writeBusy = "",
  writeError = "",
  writeEvents = [],
  writeLoading = false,
  writeRequest = null,
}) {
  const [confirming, setConfirming] = useState(false);
  const [confirmationAccepted, setConfirmationAccepted] = useState(false);
  const [confirmationAttention, setConfirmationAttention] = useState(false);
  const [selectedSourceWarehouseId, setSelectedSourceWarehouseId] = useState("");
  const bodyRef = useRef(null);
  const confirmationRef = useRef(null);
  const confirmationInputRef = useRef(null);
  const payload = source?.payloadPreview || {};
  const sourceDescription = String(
    source?.sourceDescription ?? payload.description ?? "",
  );
  const [descriptionText, setDescriptionText] = useState(sourceDescription);
  const [itemEditor, setItemEditor] = useState(null);
  const capability = source?.writeCapability || {};
  const ready = Boolean(
    source?.readinessStatus === "READY_TO_CREATE" || capability.itemEditorReady,
  );
  const presentation = getWritePresentation(writeRequest);
  const requestStatus = String(writeRequest?.status || "").toUpperCase();
  const hasWriteRequest = Boolean(writeRequest?.id || writeRequest?.status);
  const canRetry = Boolean(
    capability.canRetry && requestStatus === "FAILED",
  );
  const hasBlockingWriteRequest = hasWriteRequest && !canRetry;
  const capabilityAllowsCreate = Boolean(
    capability.canCreate ||
      capability.reasonCode === "READY" ||
      capability.reasonCode === "RETRY_READY",
  );
  const canCreate = Boolean(
    ready &&
      itemEditor?.ready &&
      capabilityAllowsCreate &&
      !hasBlockingWriteRequest,
  );
  const canReconcile = Boolean(
    capability.canReconcile ||
      String(writeRequest?.status || "").toUpperCase() ===
        "RECONCILIATION_REQUIRED",
  );
  const showTechnicalDetails = canShowTechnicalDetails();
  const confirmationMatches = confirmationAccepted;
  const descriptionChanged = descriptionText !== sourceDescription;
  const warehouseNames = [...new Set(
    (source?.items || [])
      .map((item) => String(item?.warehouseName || "").trim())
      .filter(Boolean),
  )];
  const activeWarehouses = useMemo(
    () => (warehouses || []).filter((warehouse) => warehouse.active !== false),
    [warehouses],
  );
  const fleetWarehouseId = Number(
    fleetOrderWarehouse?.mappedWarehouseId ??
      fleetOrderWarehouse?.warehouseId,
  ) || null;
  const fleetWarehouseName =
    fleetOrderWarehouse?.mappedWarehouseName ||
    fleetOrderWarehouse?.warehouseName ||
    null;
  const canManageWarehouse = Boolean(
    source?.sourceType === "SALES_ORDER" &&
      source?.preparationId &&
      !source?.alreadyCreated,
  );
  const expeditionAssignment = [
    payload.charField1,
    payload.charField2,
    payload.charField3,
  ].every((value) => String(value || "").trim().toLowerCase() === "ekspedisi");
  const confirmationWarnings = [
    descriptionChanged
      ? "Keterangan DO berbeda dari Keterangan Pesanan Penjualan."
      : null,
    warehouseNames.length > 1
      ? `Dokumen menggunakan ${warehouseNames.length} gudang berbeda.`
      : null,
    expeditionAssignment
      ? "Pengiriman menggunakan Ekspedisi pada nomor polisi, driver, dan co-driver."
      : null,
  ].filter(Boolean);
  const hasStockNotice = (source?.blockers || []).some(
    (blocker) => blocker.code === "STOCK_NOT_SAFE",
  );

  useEffect(() => {
    setConfirming(false);
    setConfirmationAccepted(false);
    setConfirmationAttention(false);
    setDescriptionText(sourceDescription);
    setItemEditor(null);
    setSelectedSourceWarehouseId(
      source?.defaultWarehouseId
        ? String(source.defaultWarehouseId)
        : source?.items?.[0]?.warehouseId
          ? String(source.items[0].warehouseId)
          : "",
    );
  }, [
    source?.key,
    source?.payloadHash,
    source?.defaultWarehouseId,
    sourceDescription,
  ]);

  useEffect(() => {
    if (!confirming) return undefined;

    setConfirmationAttention(true);

    const frameId = window.requestAnimationFrame(() => {
      const body = bodyRef.current;
      if (body) {
        body.scrollTo({
          top: body.scrollHeight,
          behavior: "smooth",
        });
      }
    });

    const focusTimer = window.setTimeout(() => {
      confirmationInputRef.current?.focus({ preventScroll: true });
    }, 450);

    const attentionTimer = window.setTimeout(() => {
      setConfirmationAttention(false);
    }, 1800);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(focusTimer);
      window.clearTimeout(attentionTimer);
    };
  }, [confirming, source?.key]);

  if (!source) return null;

  async function applySourceWarehouse(warehouseId = selectedSourceWarehouseId) {
    const normalizedWarehouseId = Number(warehouseId);
    if (!Number.isInteger(normalizedWarehouseId) || normalizedWarehouseId <= 0) {
      return;
    }

    await onSourceWarehouseChange?.(source, normalizedWarehouseId);
  }

  async function applyFleetOrderWarehouse() {
    if (!fleetWarehouseId) return;
    setSelectedSourceWarehouseId(String(fleetWarehouseId));
    await applySourceWarehouse(fleetWarehouseId);
  }

  function enterConfirmation() {
    setConfirmationAccepted(false);
    setConfirming(true);
  }

  async function confirmCreate() {
    if (!confirmationMatches || writeBusy) return;
    const result = await onCreate?.(source, {
      descriptionMode: descriptionChanged ? "CUSTOM" : "SOURCE",
      description: descriptionText,
      deliveryMode: itemEditor?.deliveryMode || "FULL",
      // Submit the complete reviewed editor state. Backend still revalidates
      // stock and remaining SO quantities, but it must not reconstruct default
      // selections from an empty manual-only payload.
      itemSelections: itemEditor?.itemSelections || [],
    });
    if (result !== false) {
      setConfirming(false);
      setConfirmationAccepted(false);
    }
  }

  const notice = presentation || {
    tone: ready && !hasStockNotice ? "ready" : "review",
    icon: ready && !hasStockNotice ? "check" : "warning",
    title: ready ? "Review item dan Qty DO" : "Dokumen belum siap dibuat",
    detail: ready
      ? "Periksa jumlah yang akan dikirim sebelum membuat Delivery Order."
      : "Selesaikan informasi yang masih kurang terlebih dahulu.",
  };

  return (
    <Dialog
      className="do-write-preview-dialog"
      onClose={onClose}
      title={`Review Item & Qty DO · ${source.sourceNumber}`}
    >
      <div className="dialog-card__body do-write-preview-dialog__body" ref={bodyRef}>
        <div className={`do-write-preview__notice is-${notice.tone}`}>
          <Icon name={notice.icon} size={18} />
          <div>
            <strong>{notice.title}</strong>
            <span>{notice.detail}</span>
            {presentation?.tone === "success" && (
              <b>
                {writeRequest?.accurateDoNumber ||
                  capability.accurateDoNumber ||
                  "Nomor DO sedang diperbarui"}
              </b>
            )}
          </div>
        </div>

        {writeError && (
          <div className="do-write-preview__action-error" role="alert">
            <Icon name="warning" size={16} />
            <span>{writeError}</span>
          </div>
        )}

        <dl className="do-write-preview__header-grid">
          <div>
            <dt>Nomor dokumen</dt>
            <dd>{source.sourceNumber || "—"}</dd>
          </div>
          <div>
            <dt>Pelanggan</dt>
            <dd>{source.customerName || "—"}</dd>
          </div>
          <div>
            <dt>Tanggal DO</dt>
            <dd>{payload.transDate || "—"}</dd>
          </div>
          <div>
            <dt>Driver</dt>
            <dd>{displayOperationalValue(payload.charField2, "Belum ditentukan")}</dd>
          </div>
          <div>
            <dt>Co-driver</dt>
            <dd>{displayCoDriverValue(payload.charField3, source.blockers)}</dd>
          </div>
          <div>
            <dt>Nomor polisi</dt>
            <dd>{displayOperationalValue(payload.charField1, "Belum ditentukan")}</dd>
          </div>
          <div>
            <dt>Gudang</dt>
            <dd>
              {warehouseNames.length === 1
                ? warehouseNames[0]
                : warehouseNames.length > 1
                  ? `${warehouseNames.length} gudang`
                  : source.defaultWarehouseName || "Belum ditentukan"}
            </dd>
          </div>
          <div>
            <dt>Cabang</dt>
            <dd>{source.branchLabel || "Mengikuti Pesanan Penjualan"}</dd>
          </div>
        </dl>

        {canManageWarehouse && (
          <section
            className="do-write-warehouse-card"
            aria-label="Pengaturan gudang Delivery Order"
          >
            <header className="do-write-warehouse-card__header">
              <div>
                <span>Gudang pengiriman</span>
                <strong>Atur gudang untuk Sales Order ini</strong>
                <p>
                  Secara default seluruh item mengikuti gudang Fleet Order.
                  Gudang dapat diganti untuk seluruh SO atau per item sebelum DO dibuat.
                </p>
              </div>
              <div className="do-write-warehouse-card__fo-default">
                <small>Default Fleet Order</small>
                <strong>{fleetWarehouseName || "Belum dipetakan"}</strong>
              </div>
            </header>

            <div className="do-write-warehouse-card__controls">
              <label>
                <span>Gudang seluruh SO</span>
                <select
                  disabled={Boolean(writeBusy)}
                  onChange={(event) =>
                    setSelectedSourceWarehouseId(event.target.value)
                  }
                  value={selectedSourceWarehouseId}
                >
                  <option value="">Pilih gudang</option>
                  {activeWarehouses.map((warehouse) => (
                    <option
                      key={warehouse.warehouseId}
                      value={warehouse.warehouseId}
                    >
                      {warehouse.warehouseName}
                    </option>
                  ))}
                </select>
              </label>

              <button
                className="primary-button do-write-warehouse-card__apply"
                disabled={!selectedSourceWarehouseId || Boolean(writeBusy)}
                onClick={() => applySourceWarehouse()}
                type="button"
              >
                {writeBusy === "warehouse"
                  ? "Memperbarui…"
                  : "Terapkan ke seluruh item"}
              </button>

              {fleetWarehouseId && (
                <button
                  className="secondary-button"
                  disabled={Boolean(writeBusy)}
                  onClick={applyFleetOrderWarehouse}
                  type="button"
                >
                  Gunakan gudang FO
                </button>
              )}
            </div>

            <div className="do-write-warehouse-card__note">
              <Icon name="info" size={16} />
              <span>
                Perubahan gudang langsung menghitung ulang stok dan kesiapan Qty DO.
                Item tertentu tetap dapat memakai gudang berbeda melalui kolom Gudang.
              </span>
            </div>
          </section>
        )}

        {source.blockers?.some((blocker) => blocker.code !== "STOCK_NOT_SAFE") && (
          <section className="do-write-preview__blockers">
            <strong>Yang perlu diselesaikan</strong>
            <ul>
              {source.blockers
                .filter((blocker) => blocker.code !== "STOCK_NOT_SAFE")
                .map((blocker) => (
                <li key={`${blocker.code}:${blocker.itemNo || ""}`}>
                  <b>{blocker.title}</b>
                  <span>{blocker.detail}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <DoItemQuantityEditor
          disabled={Boolean(writeBusy) || hasBlockingWriteRequest}
          initialSelections={(writeRequest?.itemSelections || []).filter((entry) =>
            entry?.origin ? entry.origin === "USER_EDITED" : entry?.editedManually !== false,
          )}
          items={source.items || []}
          onChange={setItemEditor}
          onWarehouseChange={(item, warehouseId) =>
            onItemWarehouseChange?.(source, item, warehouseId)
          }
          resetKey={`${source.key}:${source.payloadHash}:${writeRequest?.cycleNo || 1}`}
          warehouseBusy={writeBusy === "warehouse"}
          warehouses={activeWarehouses}
        />

        <section className="do-write-description" aria-label="Keterangan Delivery Order">
          <div className="do-write-description__heading">
            <div>
              <label htmlFor={`do-description-${source.sourceDocumentId}`}>
                Keterangan DO
              </label>
              <span>
                Diambil dari Keterangan Pesanan Penjualan. Perubahan di sini
                hanya berlaku pada Delivery Order dan tidak mengubah SO.
              </span>
            </div>
            {descriptionChanged && (
              <button
                className="text-button"
                disabled={Boolean(writeBusy)}
                onClick={() => setDescriptionText(sourceDescription)}
                type="button"
              >
                Kembalikan dari SO
              </button>
            )}
          </div>
          <textarea
            disabled={Boolean(writeBusy) || hasBlockingWriteRequest}
            id={`do-description-${source.sourceDocumentId}`}
            maxLength={5000}
            onChange={(event) => setDescriptionText(event.target.value)}
            placeholder="Tambahkan keterangan untuk Delivery Order"
            rows={3}
            value={descriptionText}
          />
          <div className="do-write-description__footer">
            <span>
              {descriptionChanged
                ? "Keterangan DO telah diubah dan akan digunakan saat transaksi dibuat."
                : "Keterangan DO akan mengikuti Pesanan Penjualan."}
            </span>
            <small>{descriptionText.length}/5000</small>
          </div>
        </section>

        {confirming && (
          <section
            className={`do-write-confirmation${confirmationAttention ? " is-attention" : ""}`}
            ref={confirmationRef}
            aria-label="Konfirmasi pembuatan DO"
          >
            <div className="do-write-confirmation__heading">
              <Icon name="warning" size={18} />
              <div>
                <strong>
                  {canRetry
                    ? "Coba buat kembali Delivery Order?"
                    : itemEditor?.deliveryMode === "PARTIAL"
                      ? "Buat Delivery Order sebagian di Accurate?"
                      : "Buat Delivery Order penuh di Accurate?"}
                </strong>
                <span>
                  {canRetry
                    ? `Percobaan sebelumnya gagal secara pasti. Sistem akan mencoba kembali ${source.sourceNumber} dengan data terbaru.`
                    : itemEditor?.deliveryMode === "PARTIAL"
                      ? `${itemEditor?.summary?.positive || 0} item akan dimasukkan ke DO dan sisanya tetap terbuka pada ${source.sourceNumber}.`
                      : `Seluruh sisa item ${source.sourceNumber} akan dimasukkan ke satu Delivery Order.`}
                </span>
              </div>
            </div>
            {confirmationWarnings.length > 0 && (
              <ul className="do-write-confirmation__warnings">
                {confirmationWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            )}
            <label className="do-write-confirmation__check">
              <input
                ref={confirmationInputRef}
                checked={confirmationAccepted}
                disabled={Boolean(writeBusy)}
                onChange={(event) =>
                  setConfirmationAccepted(event.target.checked)
                }
                type="checkbox"
              />
              <span>
                Saya sudah memeriksa pelanggan, gudang, item, Qty DO dalam
                satuan terkecil, driver, co-driver, dan nomor polisi.
              </span>
            </label>
          </section>
        )}

        {(writeLoading || writeEvents.length > 0) && (
          <details className="do-write-history">
            <summary>Riwayat proses</summary>
            {writeLoading ? (
              <p>Memuat riwayat proses…</p>
            ) : (
              <ol>
                {writeEvents.map((event) => (
                  <li key={event.id}>
                    <span>
                      {WRITE_EVENT_LABELS[event.eventType] || "Status diperbarui"}
                    </span>
                    <time>{formatWriteTimestamp(event.createdAt)}</time>
                  </li>
                ))}
              </ol>
            )}
          </details>
        )}

        {showTechnicalDetails && (
          <details className="do-write-preview__payload">
            <summary>Informasi teknis</summary>
            <pre>{JSON.stringify(payload, null, 2)}</pre>
          </details>
        )}
      </div>

      <footer className="dialog-card__actions do-write-preview-dialog__actions">
        {confirming ? (
          <>
            <button
              className="secondary-button"
              disabled={Boolean(writeBusy)}
              onClick={() => {
                setConfirming(false);
                setConfirmationAccepted(false);
              }}
              type="button"
            >
              Kembali
            </button>
            <button
              className="primary-button"
              disabled={!confirmationMatches || Boolean(writeBusy)}
              onClick={confirmCreate}
              type="button"
            >
              {writeBusy === "create"
                ? "Membuat DO…"
                : canRetry
                  ? "Coba Buat Delivery Order"
                  : "Buat Delivery Order"}
            </button>
          </>
        ) : (
          <>
            <button
              className={canCreate || canReconcile ? "secondary-button" : "primary-button"}
              disabled={Boolean(writeBusy)}
              onClick={onClose}
              type="button"
            >
              Tutup
            </button>
            {canReconcile && (
              <button
                className="primary-button"
                disabled={Boolean(writeBusy)}
                onClick={() => onReconcile?.(source)}
                type="button"
              >
                {writeBusy === "reconcile"
                  ? "Memeriksa hasil…"
                  : "Periksa hasil di Accurate"}
              </button>
            )}
            {ready && !hasBlockingWriteRequest && !canReconcile && (
              <div className="do-write-preview__create-action">
                {!canCreate && (
                  <span role="status">
                    {capability.reason ||
                      "Pembuatan DO belum tersedia untuk dokumen ini."}
                  </span>
                )}
                <button
                  className="primary-button"
                  disabled={!canCreate || Boolean(writeBusy)}
                  onClick={enterConfirmation}
                  title={!canCreate ? capability.reason || undefined : undefined}
                  type="button"
                >
                  {canRetry ? "Coba Buat Lagi" : "Lanjutkan ke konfirmasi"}
                </button>
              </div>
            )}
          </>
        )}
      </footer>
    </Dialog>
  );
}

export function DoWriteContractPreview({
  contract,
  contractError = "",
  depotWarehouse = null,
  loading = false,
  mappingBusy = false,
  onCheckProfile,
  onMapWarehouse,
  onRetryContract,
  profileBusy = false,
  profileError = "",
  profileStatus = null,
  warehouses = [],
}) {
  const summary = contract?.summary || {};
  const sources = contract?.sources || [];
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");

  const profile = useMemo(
    () => getProfilePresentation(profileStatus, profileBusy, profileError),
    [profileBusy, profileError, profileStatus],
  );

  const globalBlockerCodes = useMemo(
    () => getGlobalBlockerCodes(sources),
    [sources],
  );
  const assignmentBlockers = useMemo(
    () => getAssignmentBlockers(globalBlockerCodes),
    [globalBlockerCodes],
  );
  const globalWarehouseBlock = globalBlockerCodes.has("WAREHOUSE_MAPPING_REQUIRED");

  const activeWarehouses = useMemo(
    () => warehouses.filter((warehouse) => warehouse.active),
    [warehouses],
  );

  useEffect(() => {
    setSelectedWarehouseId(
      contract?.fleetOrder?.warehouseId
        ? String(contract.fleetOrder.warehouseId)
        : "",
    );
  }, [contract?.fleetOrder?.warehouseId]);

  if (!contract && loading) {
    return (
      <section
        className="do-readiness-panel do-readiness-panel--loading"
        aria-live="polite"
      >
        <Icon name="sync" size={18} />
        <span>Memeriksa kesiapan Delivery Order…</span>
      </section>
    );
  }

  if (!contract && contractError) {
    return (
      <section className="do-readiness-panel do-readiness-panel--error" role="alert">
        <Icon name="warning" size={20} />
        <div>
          <strong>Kesiapan Delivery Order belum dapat dimuat</strong>
          <span>Coba perbarui data. Hubungi administrator bila masalah berulang.</span>
        </div>
        <button className="secondary-button" onClick={onRetryContract} type="button">
          Coba lagi
        </button>
      </section>
    );
  }

  if (!contract) return null;

  const fleetOrderUrl = contract.fleetOrder?.id
    ? `/fleet-orders/${encodeURIComponent(contract.fleetOrder.id)}?tab=summary`
    : null;

  return (
    <section
      className="do-readiness-panel"
      aria-label="Kesiapan Pembuatan Delivery Order"
    >
      <header className="do-readiness-panel__header">
        <div>
          <span>Kesiapan Pembuatan DO</span>
          <h2>Periksa dokumen sebelum diproses</h2>
          <p>Selesaikan informasi yang belum siap sebelum membuat Delivery Order.</p>
        </div>
        <div
          className={`do-readiness-profile do-readiness-profile--${profile.tone}`}
        >
          <span className="do-readiness-profile__dot" />
          <div>
            <small>Koneksi Accurate</small>
            <strong>{profile.label}</strong>
            <span>{profile.detail}</span>
          </div>
          {!profileStatus?.ready && (
            <button
              className="secondary-button"
              disabled={profileBusy}
              onClick={onCheckProfile}
              type="button"
            >
              {profileBusy ? "Memeriksa…" : "Coba lagi"}
            </button>
          )}
        </div>
      </header>

      <div className="do-readiness-panel__metrics">
        <div>
          <span>Dokumen sumber</span>
          <strong>{summary.total || 0}</strong>
        </div>
        <div className="is-attention">
          <span>Belum siap</span>
          <strong>{summary.blocked || 0}</strong>
        </div>
        <div className="is-ready">
          <span>Siap direview</span>
          <strong>{summary.ready || 0}</strong>
        </div>
        <div>
          <span>DO sudah dibuat</span>
          <strong>{summary.created || 0}</strong>
        </div>
      </div>

      {assignmentBlockers.length > 0 && (
        <div className="do-readiness-panel__global-blocker do-readiness-panel__global-blocker--assignment" role="alert">
          <Icon name="truck" size={18} />
          <div className="do-readiness-panel__global-copy">
            <strong>Fleet Order belum siap untuk pembuatan DO</strong>
            <ul>
              {assignmentBlockers.map((blocker) => (
                <li key={blocker.title}>
                  <b>{blocker.title}</b>
                  <span>{blocker.detail}</span>
                </li>
              ))}
            </ul>
          </div>
          {fleetOrderUrl && (
            <a className="secondary-button" href={fleetOrderUrl}>
              Atur di Fleet Order
            </a>
          )}
        </div>
      )}

      {globalWarehouseBlock && (
        <div className="do-readiness-panel__global-blocker" role="alert">
          <Icon name="warning" size={18} />
          <div className="do-readiness-panel__global-copy">
            <strong>Gudang Accurate untuk depot belum ditentukan</strong>
            <span>
              Pilih gudang untuk {depotWarehouse?.depotName || "depot Fleet Order"}.
              Pilihan ini berlaku untuk seluruh dokumen pada Fleet Order.
            </span>
          </div>
          <div className="do-readiness-panel__mapping-control">
            <label>
              <span>Gudang Accurate</span>
              <select
                disabled={mappingBusy}
                onChange={(event) => setSelectedWarehouseId(event.target.value)}
                value={selectedWarehouseId}
              >
                <option value="">Pilih gudang</option>
                {activeWarehouses.map((warehouse) => (
                  <option
                    key={warehouse.warehouseId}
                    value={warehouse.warehouseId}
                  >
                    {warehouse.warehouseName}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="primary-button"
              disabled={mappingBusy || !selectedWarehouseId}
              onClick={() => onMapWarehouse(selectedWarehouseId)}
              type="button"
            >
              {mappingBusy ? "Menyimpan…" : "Tetapkan gudang"}
            </button>
          </div>
        </div>
      )}

      {contractError && (
        <div className="do-readiness-panel__contract-note" role="status">
          <Icon name="warning" size={15} />
          <span>Data terakhir tetap ditampilkan. Coba perbarui bila diperlukan.</span>
          <button onClick={onRetryContract} type="button">
            Muat ulang
          </button>
        </div>
      )}

      <div className="do-readiness-panel__facts-shell">
        <div className="do-readiness-panel__facts-heading">
          <div>
            <strong>Informasi Delivery Order</strong>
            <span>Data ini akan dikirim ke Accurate saat Delivery Order dibuat.</span>
          </div>
        </div>

        <div
          className="do-readiness-panel__facts"
          aria-label="Informasi Delivery Order"
        >
          <div>
            <span className="do-readiness-panel__fact-icon" aria-hidden="true">
              <Icon name="clock" size={16} />
            </span>
            <span>
              <small>Tanggal DO</small>
              <strong>{formatDisplayDate(contract.fleetOrder?.transDate)}</strong>
            </span>
          </div>

          <div>
            <span className="do-readiness-panel__fact-icon" aria-hidden="true">
              <Icon name="user" size={16} />
            </span>
            <span>
              <small>Tim pengiriman</small>
              <strong>
                {displayOperationalValue(
                  contract.fleetOrder?.driverName,
                  "Driver belum ditentukan",
                )}
              </strong>
              <em>
                Co-driver: {displayCoDriverValue(
                  contract.fleetOrder?.coDriverName,
                  (contract.sources || []).flatMap((source) => source.blockers || []),
                )}
              </em>
            </span>
          </div>

          <div>
            <span className="do-readiness-panel__fact-icon" aria-hidden="true">
              <Icon name="truck" size={16} />
            </span>
            <span>
              <small>Kendaraan</small>
              <strong>
                {displayOperationalValue(
                  contract.fleetOrder?.vehiclePlate,
                  "Nomor polisi belum ditentukan",
                )}
              </strong>
            </span>
          </div>

          <div>
            <span className="do-readiness-panel__fact-icon" aria-hidden="true">
              <Icon name="database" size={16} />
            </span>
            <span>
              <small>Gudang Accurate</small>
              <strong>
                {contract.fleetOrder?.warehouseName || "Belum ditentukan"}
              </strong>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export { STATUS_LABELS };
